import { computed, Inject, Injectable, signal } from '@angular/core';
import { JSBridgeService } from '../core/jsbridge.service';
import { AfdelingWM, AfdModule } from '@etron/typings';
import { cAfdelingWM } from '@wasm/KopWeb';
import { ModulesRepoService } from './modules-repo.service';
import { KopConfigService } from '../core/kop-config.service';
import { ServerAPI } from '../serverapi';
import { HwdConfigService } from '../kanaal/hwd-config.service';
import { HwdSerializable, HwdSysteem } from '../kanaal/hdw-config/HdwSysteem';
import { DialogService } from '@progress/kendo-angular-dialog';
import { TitleDialogComponent } from '../afdeling/title-dialog/title-dialog.component';

@Injectable({
  providedIn: null
})
export class KopModulesService {

  constructor(private jsBridgeService: JSBridgeService, private modulesRepo: ModulesRepoService,
    private kopConfigService: KopConfigService, public hdwConfigService: HwdConfigService, @Inject('ServerApi') private electronApi: ServerAPI
    , private dialogService: DialogService) {
    this.jsBridgeService.getBridge().subscribe(bridge => {
      this.load();
    });

    if (window.electronAPI != undefined) {
      window.electronAPI.saveCallBack(() => {
        this.save("");
      })
    }
  }

  public afdelings = signal<Record<string, AfdelingWM>>({}, { equal: (a, b) => false });
  public selectedAfdModule = signal<AfdModule | undefined>(undefined, { equal: (a, b) => false });
  public isEditingModule = signal(false);

  public afdModules = computed(() => {
    let selectedAfd = this.kopConfigService.selectedAfd();
    if (selectedAfd != undefined) {
      let uuid = selectedAfd.afdeling().uuid.toString();
      let afdWM = this.afdelings()[uuid];
      if (afdWM != undefined) {
        return Object.assign([], afdWM.modules) as AfdModule[];
      }
    }
    return [];
  });


  cleanModules() {
    let kopAfdelings = this.kopConfigService.afdelings();
    let afdelings = this.afdelings();
    for (let uuid in afdelings) {
      if (kopAfdelings.findIndex(afd => afd.afdeling().uuid.toString() == uuid) < 0) {
        console.log(`Not found afd: ${uuid}. removing modules ...`);
        this.deleteAfdelingModules(uuid);
      }
    }
  }

  deleteAfdelingModules(uuid: string) {
    let afdWM = this.afdelings()[uuid];
    if (afdWM != undefined) {
      this.afdelings.update(afds => {
        delete afds[uuid];
        return afds;
      });
    }
  }

  deleteModule(module: AfdModule, removeIOs: boolean = false) {
    let uuid = this.kopConfigService.selectedAfd()?.afdeling().uuid.toString();
    if (uuid != undefined) {
      let afd = this.afdelings()[uuid];
      if (afd != undefined) {

        let idx = afd.modules.findIndex(m => m.meta.key == module.meta.key);
        if (idx >= 0) {
          afd.modules.splice(idx, 1);
          if (this.selectedAfdModule()?.meta.key == module.meta.key) {
            this.selectedAfdModule.set(undefined);
          }
          this.afdelings.update(afds => {
            return afds;
          });

          if (removeIOs) {
            this.kopConfigService.applyAfdUpdate(afd => {
              if (module.meta.allAnalogIngagen != undefined) {
                for (let ai of module.meta.allAnalogIngagen) {
                  this.kopConfigService.eraseAI(this.kopConfigService.selectedAfd(), ai);
                }
              }
              if (module.meta.allServos != undefined) {
                for (let servo of module.meta.allServos) {
                  this.kopConfigService.eraseServo(this.kopConfigService.selectedAfd(), servo);
                }
              }
              if (module.meta.allBijzPrgm != undefined) {
                for (let prgm of module.meta.allBijzPrgm) {
                  this.kopConfigService.eraseBijzPrgm(prgm);
                }
              }
              if (module.meta.alarms != undefined) {
                for (let alarm of module.meta.alarms) {
                  this.kopConfigService.eraseAlarm(alarm);
                }
              }
              if (module.meta.allLosDefsIndexes != undefined) {
                let typeMap = this.kopConfigService.losDefTypeMap();
                for (let losdefIndex of module.meta.allLosDefsIndexes) {
                  let type = typeMap.get(losdefIndex);
                  if (type == undefined) {
                    continue;
                  }
                  this.kopConfigService.eraseLosDef(this.kopConfigService.selectedAfd(), losdefIndex, type);
                }
              }
              return afd;
            });

          }
        }
      }
    }
  }

  addModule(moduleKey: string, afd: cAfdelingWM) {
    let module = this.modulesRepo.getModule(moduleKey);
    let uuid = afd.afdeling().uuid.toString();
    if (module != undefined) {
      let newModule = {
        meta: module,
        titel: module.name
      };
      this.afdelings.update(afds => {
        let afdWM = afds[uuid];
        if (afdWM == undefined) {
          afdWM = {
            afdelingUUID: uuid,
            modules: []
          };
          afds[uuid] = afdWM;
        }
        afdWM.modules.push(newModule);
        return afds;
      });
      this.selectedAfdModule.set(newModule);
      return newModule;
    }

    return undefined;
  }

  paste(afdUUID: string, modules: Array<AfdModule>, replace: boolean = true) {
    this.afdelings.update(afds => {
      let newM = afds[afdUUID];
      if (newM != undefined) {
        if (replace) {
          newM.modules = modules;
        } else {
          newM.modules = newM.modules.concat(modules);
        }
      } else {
        afds[afdUUID] = {
          afdelingUUID: afdUUID,
          modules: modules
        };

      }
      return afds;
    });

  }

  async load() {
    let klantNo = this.jsBridgeService.getKlantNo();
    let json: string | null = null;
    if (this.jsBridgeService.loadFromMemory) {
      console.log("Loading Modules from memory");
      json = window.localStorage.getItem(`${klantNo}.modules.json`);
    } else {
      let wasmModule = this.jsBridgeService.wasmModule!;
      if (wasmModule.FS.analyzePath(`/working/modules/${klantNo}.modules.json`, true).exists) {
        json = wasmModule.FS.readFile(`/working/modules/${klantNo}.modules.json`, {
          encoding: 'utf8'
        });

      }
    }
    if (json != null && json.length > 0) {
      let payload: any = JSON.parse(json);
      let afdelings;
      if (payload.hasOwnProperty('modules')) {
        afdelings = payload.modules as Record<string, AfdelingWM>;
        let hwConfig = payload.hardware;
        if (hwConfig != undefined) {
          this.hdwConfigService.system.set(new HwdSysteem(undefined, hwConfig as HwdSerializable));
        }
      } else {
        afdelings = payload as Record<string, AfdelingWM>;
      }
      this.afdelings.set(afdelings);
    }
    this.cleanModules();
  }


  serialize() {
    let afdelings = this.afdelings();
    let hwConfig = this.hdwConfigService.system()?.toSerializable();
    let saveObj = {
      modules: afdelings,
      hardware: hwConfig
    };
    let json = JSON.stringify(saveObj, undefined, 2);
    return json;
  }

  async saveWithComments() {
    const dialogRef = this.dialogService.open({
      width: 400,
      content: TitleDialogComponent,
    });

    const titleDlg = dialogRef.content.instance as TitleDialogComponent;
    titleDlg.dialogNameKey.set('Comments');
    titleDlg.title = "";
    titleDlg.maxLength = 500;
    dialogRef.result.subscribe(async (r: any) => {
      if (r.text == "Submit") {
        let comments = r.naam;
        await this.save(comments);
      }
    });
  }

  async save(comment: string, history: boolean = true) {
    let klantNo = this.jsBridgeService.getKlantNo();
    let json = this.serialize();
    let bytes = new TextEncoder().encode(json);
    //await this.electronApi.saveFile(`modules/${klantNo}.modules.json`, bytes);
    let sxbData = await this.jsBridgeService.save(false);
    let ret = await this.electronApi.saveKop(klantNo, sxbData, bytes, comment, history);
    if (ret) {
      this.hdwConfigService.isDirty.set(false);
      this.kopConfigService.isDirty.set(false);
    }
    // this.selectedAfdModule.set(undefined);
  }
}
